import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface PatientFileRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  kind: string | null;
  filename: string | null;
  url: string | null;
  tag: string | null;
  mime_type: string | null;
  description: string | null;
  starred: number | null; // 0 or 1
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientNoteRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  title: string | null;
  body: string | null;
  note_type: string | null; // 'general' | 'clinical' | 'follow_up' | 'admin'
  visibility: string | null; // 'team' | 'private' | 'front_desk'
  alert_level: string | null; // 'none' | 'info' | 'warning' | 'critical'
  author_user_id: string | null;
  author_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientPrescriptionRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  item_name: string | null;
  item_details: string | null;
  // PRD.189 — optional Medicine inventory reference + snapshots
  inventory_item_id: string | null;
  free_text_name: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  generic_name_snapshot: string | null;
  brand_name_snapshot: string | null;
  strength_snapshot: string | null;
  dosage_form_snapshot: string | null;
  route_snapshot: string | null;
  unit_snapshot: string | null;
  default_instruction_snapshot: string | null;
  default_dosage_snapshot: string | null;
  usage_snapshot: string | null;
  dose_snapshot: string | null;
  dose_unit_snapshot: string | null;
  frequency_snapshot: string | null;
  duration_snapshot: string | null;
  intake_timing: string | null; // 'before_food' | 'after_food' | 'with_food' | 'empty_stomach' | 'anytime'
  dose_quantity: string | null;
  schedule_morning: number | null; // 0 or 1
  schedule_afternoon: number | null;
  schedule_night: number | null;
  duration_value: number | null;
  duration_unit: string | null; // 'days' | 'weeks' | 'months'
  instructions: string | null;
  sort_order: number | null;
  // PRD.219 — origin prescription template id when applied from a template;
  // null for manual rows.
  source_template_id: string | null;
  // PRD.217 — provenance + lifecycle.
  // source: 'ai' | 'clinician' | 'template' | null (null treated as clinician)
  // status: 'draft' | 'final' | 'cancelled' (server defaults to 'final')
  // reviewed_*: stamped on Accept when an AI draft is promoted to final.
  source: string | null;
  status: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  author_user_id: string | null;
  author_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientFormRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  name: string | null;
  tag: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

export function qPatientFiles(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_files', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_files WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_files'],
  };
}

export function qPatientFilesByEncounter(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['patient_files', 'by-encounter', patientId, encounterId] as const,
    sql: `SELECT * FROM patient_files WHERE patient_id = ? AND encounter_id = ? ORDER BY created_at DESC`,
    params: [patientId, encounterId],
    watch: ['patient_files'],
  };
}

export function qPatientNotes(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_notes WHERE patient_id = ? ORDER BY updated_at DESC`,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

export function qNotesByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_notes WHERE encounter_id = ? ORDER BY created_at DESC`,
    params: [encounterId],
    watch: ['patient_notes'],
  };
}

export function qPatientAlertNotes(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'alerts', patientId] as const,
    sql: `SELECT * FROM patient_notes
          WHERE patient_id = ?
            AND alert_level IN ('info', 'warning', 'critical')
            AND visibility IN ('team', 'front_desk')
          ORDER BY created_at DESC
          LIMIT 3`,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

export function qNotesPreviewByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'preview-by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_notes WHERE encounter_id = ? ORDER BY created_at DESC LIMIT 3`,
    params: [encounterId],
    watch: ['patient_notes'],
  };
}

/**
 * PRD.210 — Invoice "Add Items from Prescription" candidate row.
 *
 * One row per inventory-bound prescription line for the current
 * patient + encounter, joined with the live inventory_items metadata
 * needed to render a Medicine/Product badge, surface a SKU + unit, and
 * fall back to the catalog's selling price when the prescription line
 * itself does not carry one.
 *
 * Free-text prescription lines are intentionally excluded — PRD §6.
 */
export interface InvoicePrescriptionCandidateRow {
  prescription_id: string;
  patient_id: string | null;
  encounter_id: string | null;
  // Prescription snapshot fields (PRD.189 / PRD.198)
  inventory_item_id: string | null;
  item_name: string | null;
  item_details: string | null;
  free_text_name: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  brand_name_snapshot: string | null;
  generic_name_snapshot: string | null;
  strength_snapshot: string | null;
  dosage_form_snapshot: string | null;
  route_snapshot: string | null;
  unit_snapshot: string | null;
  default_instruction_snapshot: string | null;
  default_dosage_snapshot: string | null;
  usage_snapshot: string | null;
  dose_snapshot: string | null;
  dose_unit_snapshot: string | null;
  frequency_snapshot: string | null;
  duration_snapshot: string | null;
  // Inventory join (live values; the snapshot fields above are still
  // the historical record and should be preferred for display).
  inventory_item_type: string | null; // 'medicine' | 'product' | ...
  inventory_selling_price: number | null;
  inventory_unit: string | null;
  inventory_status: string | null;
}

/**
 * PRD.210 — Inventory-bound prescription lines for the current visit
 * that the user can add to an invoice. Returned ordered the same way
 * the prescription editor displays them so the modal feels familiar.
 *
 * Scoped by patient + visit DATE, OR the invoice's encounter_id. An
 * invoice created from the Queue flow carries no encounter_id (the
 * drawer gets no encounter prop), so an exact encounter_id match alone
 * would drop every prescription even when they were written the same
 * day — hence the date match, which mirrors the invoice list
 * (qInvoicesByPatientAndDate, PRD.229). The encounter_id match is kept
 * as a union so a visit-note invoice still picks up its own encounter's
 * prescriptions even if the derived dates diverge (e.g. a prescription
 * added to the encounter on a later day). The prescription's date is
 * its encounter's started_at, falling back to created_at when the
 * encounter row isn't available.
 */
export function qInvoicePrescriptionCandidates(
  patientId: string,
  dateIso: string,
  encounterId: string,
): QuerySpec<[string, string, string, string]> {
  return {
    key: ['invoice-prescription-candidates', patientId, dateIso, encounterId] as const,
    sql: `
      SELECT
        p.id                              AS prescription_id,
        p.patient_id                      AS patient_id,
        p.encounter_id                    AS encounter_id,
        p.inventory_item_id               AS inventory_item_id,
        p.item_name                       AS item_name,
        p.item_details                    AS item_details,
        p.free_text_name                  AS free_text_name,
        p.item_name_snapshot              AS item_name_snapshot,
        p.sku_snapshot                    AS sku_snapshot,
        p.brand_name_snapshot             AS brand_name_snapshot,
        p.generic_name_snapshot           AS generic_name_snapshot,
        p.strength_snapshot               AS strength_snapshot,
        p.dosage_form_snapshot            AS dosage_form_snapshot,
        p.route_snapshot                  AS route_snapshot,
        p.unit_snapshot                   AS unit_snapshot,
        p.default_instruction_snapshot    AS default_instruction_snapshot,
        p.default_dosage_snapshot         AS default_dosage_snapshot,
        p.usage_snapshot                  AS usage_snapshot,
        p.dose_snapshot                   AS dose_snapshot,
        p.dose_unit_snapshot              AS dose_unit_snapshot,
        p.frequency_snapshot              AS frequency_snapshot,
        p.duration_snapshot               AS duration_snapshot,
        inv.item_type                     AS inventory_item_type,
        inv.selling_price                 AS inventory_selling_price,
        inv.inventory_unit                AS inventory_unit,
        inv.status                        AS inventory_status
      FROM patient_prescriptions p
      LEFT JOIN inventory_items inv ON inv.id = p.inventory_item_id
      LEFT JOIN patient_encounters e ON e.id = p.encounter_id
      WHERE p.patient_id = ?
        AND p.inventory_item_id IS NOT NULL
        -- PRD.217 §14 — exclude AI drafts from billing candidates. Old rows
        -- with null status backfill to 'final'.
        AND (p.status IS NULL OR p.status = 'final')
        -- Match the visit day OR the invoice's own encounter (see note above).
        AND (
          date(COALESCE(e.started_at, p.created_at)) = date(?)
          OR (? <> '' AND p.encounter_id = ?)
        )
      ORDER BY p.sort_order ASC, p.created_at ASC
    `,
    params: [patientId, dateIso, encounterId, encounterId],
    watch: ['patient_prescriptions', 'inventory_items', 'patient_encounters'],
  };
}

export function qPrescriptionsByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_prescriptions', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_prescriptions WHERE encounter_id = ? ORDER BY sort_order ASC, created_at ASC`,
    params: [encounterId],
    watch: ['patient_prescriptions'],
  };
}

export function qPrescriptionsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_prescriptions', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_prescriptions WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_prescriptions'],
  };
}

export function qPatientForms(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_forms', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_forms WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_forms'],
  };
}
